import _ from 'lodash';
import config from '../config';
import message from 'framework/modules/message';
import appOptions from './appOptions';
import constants from './constants';
import storage from './storage';

let utility = {
    addComplianceType(response) {
        let feeIndex;
        // modify fees and add complianceType to each fee to make it available for the fee partial
        let complianceType;

        // the quote can either be attached at the root level or nested under 'quote' object
        let data = _.isUndefined(response.quote) ? response : response.quote;

        complianceType = _.isUndefined(data.complianceType)
            ? ''
            : data.complianceType;

        if (!_.isUndefined(data.fees)) {
            for (feeIndex = 0; feeIndex < data.fees.length; feeIndex++) {
                data.fees[feeIndex].complianceType = complianceType;
            }
        }

        return response;
    },

    /**
     * this method will need to be deprecated
     * use isCheckoutType, isOlbCheckout, isVasCart instead
     */
    doesQuoteExist() {
        return this.isOlpCheckout();
    },

    isVasCart() {
        return _.isUndefined(appOptions.get('vasCart'))
            ? false
            : appOptions.get('vasCart');
    },

    isRequestBookingFlow() {
        return _.isUndefined(appOptions.get('requestBooking'))
            ? false
            : appOptions.get('requestBooking');
    },

    isVRBO() {
        return appOptions.get('site')?.toUpperCase() === 'VRBO';
    },

    /**
     * Check whether the countrycode is AU or NZ
     * @returns boolean - true if the countrycode is AU or NZ, otherwise false.
     */
    isAuNzPos() {
        return (
            appOptions.get('countryCode') === constants.COUNTRY_CODES.NZ ||
            appOptions.get('countryCode') === constants.COUNTRY_CODES.AU
        );
    },

    registerDiscountFee(fees) {
        // need to add flag to totalAmount so that we know whether to show amount before discounts
        let discountFee = _.find(
            fees,
            (fee) => _.get(fee, 'type', '').toLowerCase() === 'discount'
        );
        let totalAmount = _.find(
            fees,
            (fee) => _.get(fee, 'type', '').toLowerCase() === 'rental_amount'
        );

        if (!_.isUndefined(totalAmount)) {
            totalAmount.hasDiscount = !_.isUndefined(discountFee);
        }
    },

    /**
     * this function takes an array of views and runs validate on them.
     * @param  {array} views array of views
     * @return {boolean}     true if all views are valid, false otherwise
     */
    validate(views) {
        let allViewsValid = true;
        let i;

        if (!_.isArray(views)) {
            return false;
        }
        for (i = 0; i < views.length; i++) {
            if (_.isFunction(views[i].validate) && !views[i].validate()) {
                allViewsValid = false;
            }
        }
        return allViewsValid;
    },

    /**
     * Check whether this is an OLP Page
     * @returns boolean - true if this is a OLP checkout, otherwise false.
     */
    isOlpCheckout() {
        return (
            appOptions.get('checkoutType') ===
            constants.CHECKOUT_TYPES.OLP_CHECKOUT
        );
    },

    /**
     * Check whether this is a VAS Cart Page
     * @returns boolean - true if this is a VAS Card checkout, otherwise false.
     */
    isVasCartCheckout() {
        return (
            appOptions.get('checkoutType') ===
            constants.CHECKOUT_TYPES.VAS_CART_CHECKOUT
        );
    },

    /**
     * Check whether this is an OLP Page that has Offline Payments enabled
     * @returns boolean - true if this is a OLP checkout with Offline Payments enabled, otherwise false.
     */
    isOlpCheckoutWithOfpOptions(checkoutModel) {
        return (
            this.isOlpCheckout() &&
            checkoutModel.hasOfflinePaymentMethodsEnabled()
        );
    },

    /**
     * Check whether this is an OLB Page
     * @returns boolean - true if this is a OLB checkout, otherwise false.
     */
    isOlbCheckout() {
        return (
            appOptions.get('checkoutType') ===
            constants.CHECKOUT_TYPES.OLB_CHECKOUT
        );
    },

    isCheckoutType(checkoutType) {
        return appOptions.get('checkoutType') === checkoutType;
    },

    isMobileViewport() {
        return document.documentElement.clientWidth < 768;
    },

    isTabletViewport() {
        return (
            document.documentElement.clientWidth >= 768 &&
            document.documentElement.clientWidth < 992
        );
    },

    isMobileOrTabletViewport() {
        return this.isMobileViewport() || this.isTabletViewport();
    },

    isNativeMobileRequest() {
        return this.isNativeMobileAndroid() || this.isNativeMobileIOS();
    },

    isNativeMobileAndroid() {
        let androidVrbexWebview;
        if (config.userAgent) {
            androidVrbexWebview =
                config.userAgent.match(/\bapp\b\.\bwebview\b/) &&
                config.userAgent.match(/Android/i);
        }
        try {
            return (
                !_.isEmpty(config.androidVersion) ||
                !_.isEmpty(
                    localStorage.getItem('isNativeMobileAndroidRequestCached')
                ) ||
                !_.isEmpty(androidVrbexWebview)
            );
        } catch (e) {
            return false;
        }
    },

    /**
     * Verifies if native request supports native confirmation
     * @returns {true| if request is native and if request is android olp than android native app version is greater than 2019.18}
     */
    isNativeConfirmationSupported() {
        if (
            (this.isNativeMobileIOS() ||
                (this.isNativeMobileAndroid() && !this.isOlpCheckout()) ||
                (this.isNativeMobileAndroid() &&
                    this.isOlpCheckout() &&
                    config.isNativeAndroidOLPConfirmSupported)) &&
            !this.isNativeVrbexWebview()
        ) {
            return true;
        }
        return false;
    },

    isNativeMobileIOS() {
        let iosVrbexWebview;
        if (config.userAgent) {
            iosVrbexWebview =
                config.userAgent.match(/\bapp\b\.\bwebview\b/) &&
                config.userAgent.match(/iPhone|iPad|iPod/i);
        }
        try {
            return (
                !_.isEmpty(config.iosVersion) ||
                !_.isEmpty(
                    localStorage.getItem('isNativeMobileIOSRequestCached')
                ) ||
                !_.isEmpty(iosVrbexWebview)
            );
        } catch (e) {
            return false;
        }
    },

    isNativeVrbexWebview() {
        let nativeVrbexWebview;
        if (config.userAgent) {
            nativeVrbexWebview = config.userAgent.match(/\bapp\b\.\bwebview\b/);
        }
        return !_.isEmpty(nativeVrbexWebview);
    },

    // Supports iOS 2.0 and later
    getClientiOSversion() {
        let v;

        if (/iP(hone|od|ad)/.test(navigator.platform)) {
            v = navigator.appVersion.match(/OS (\d+)_(\d+)_?(\d+)?/);
            return [
                parseInt(v[1], 10),
                parseInt(v[2], 10),
                parseInt(v[3] || 0, 10),
            ];
        }

        return v;
    },
    isChromeiOS() {
        return !!(this.getUserAgent() || '').match('CriOS');
    },
    isSafariIOS() {
        const ua = this.getUserAgent() || '';
        return (
            this.getClientiOSversion() &&
            /Version\//.test(ua) &&
            /Safari\//.test(ua)
        );
    },
    /**
     * TRAVCHK-4910
     * determine if local currency is different from traveler currency
     * shared functionality between test attributes for analytics and sidebar view
     * @return {Boolean} whether or not local currency does not equal travler currency
     */
    isCurrencyDifferent(currencyCode, quoteTotals) {
        // if we have no quote totals then return
        if (_.isUndefined(currencyCode) || _.isUndefined(quoteTotals)) {
            return false;
        }

        // if we have neither the subtotal amount or the amountForTravelerInTravelerCurrency amount, then return false
        if (_.isUndefined(quoteTotals.amountForTravelerInTravelerCurrency)) {
            return false;
        }

        // if total and local currency total are different, then show local currency
        return (
            currencyCode !==
            quoteTotals.amountForTravelerInTravelerCurrency.currencyCode
        );
    },

    /**
     * extract the value for a property found by propertyPath in object.
     *
     * Allows for a safe traversal of nested objects and extraction of a nested value.
     *
     * NOTE: lodash >= 3.10.x contains a "get", or "getPath", method with functionality that getNestedProperty mimics.  We should no
     * longer use this once we've upgraded our version with this supported method.
     *
     * Usage
     *
     * For Foo.Bar.Baz.Bap = "bip",
     *
     *     getNestedProperty(Foo, "") -> undefined
     *     getNestedProperty(Foo, "Bar") -> Baz
     *     getNestedProperty(Foo, "Bar.Baz.Bap") -> "bip"
     *     getNestedProperty(Foo, null) -> null
     *     getNestedProperty(Foo, []) -> null
     *     getNestedProperty(Foo, {}) -> null
     *
     * @param object possibly containing the nested property defined by propertyPath.
     * @param propertyPath period-delimited string denoting the path to for a nested property.
     * @returns {*} value of the nested property, if found; null, otherwise.
     */
    getNestedProperty(object, propertyPath) {
        let nestedProperty;
        let firstDelimiterIndex;

        if (_.isString(propertyPath)) {
            if (propertyPath.indexOf('.') > -1) {
                firstDelimiterIndex = propertyPath.indexOf('.');
                nestedProperty =
                    object[propertyPath.substring(0, firstDelimiterIndex)];

                if (_.isObject(nestedProperty)) {
                    return this.getNestedProperty(
                        nestedProperty,
                        propertyPath.substring(
                            firstDelimiterIndex + 1,
                            propertyPath.length
                        )
                    );
                }

                return null;
            }

            return object[propertyPath];
        }

        return null;
    },

    dispatchEvent(element, eventName) {
        try {
            element.dispatchEvent(new Event(eventName));
        } catch (e) {
            // Support for IE
            const evt = document.createEvent('HTMLEvents');
            evt.initEvent(eventName, true, true);
            element.dispatchEvent(evt);
        }
    },

    getTaxDisclaimer() {
        const site = appOptions.get('site');
        const distributionChannel = appOptions.get('distributionChannel');
        const siteLowerCased = site && site.toLowerCase();
        const distributionChannelLowerCased =
            distributionChannel && distributionChannel.toLowerCase();

        if (_.isUndefined(distributionChannelLowerCased)) {
            return '';
        }

        if (
            distributionChannelLowerCased ===
                constants.DISTRIBUTION_CHANNELS.EXPEDIA &&
            siteLowerCased === constants.SITES.ABRITEL
        ) {
            return message.read('traveler.checkout.tax.disclaimer');
        }

        return '';
    },

    getBaseBizopUrl() {
        return 'baseBizopUrl';
    },

    getBrowserAnimationEndEvent() {
        // https://davidwalsh.name/css-animation-callback
        let t;
        let el = document.createElement('fakeelement');
        let animations = {
            animation: 'animationend',
            oAnimation: 'oAnimationEnd',
            MSAnimation: 'MSAnimationEnd',
            webkitAnimation: 'webkitAnimationEnd',
        };

        for (t in animations) {
            if (el.style[t] !== 'undefined') {
                return animations[t];
            }
        }
        return '';
    },

    getPaymentRequests(checkoutModel) {
        return _.get(checkoutModel.get('quote'), 'paymentRequests');
    },

    getCSPriceDetailsEdapAttribute(checkoutModel, attribute) {
        if (checkoutModel.get('priceDetails')?.edapQuoteSuccess) {
            const pdEdapSuccess = JSON.parse(
                checkoutModel.get('priceDetails').edapQuoteSuccess
            );
            return _.get(pdEdapSuccess, attribute);
        }
        return null;
    },

    determineFullorderrentalrate(checkoutModel) {
        // TODO isCesSupported Clean up
        const pdForr = this.getCSPriceDetailsEdapAttribute(
            checkoutModel,
            'fullorderrentalrate'
        );
        if (pdForr) {
            const parsedPDForr = parseFloat(pdForr); // convert to decimal to mimic original functionality
            return isNaN(parsedPDForr) ? 0.0 : parsedPDForr;
        }
        // check if rental amount is available
        const rentalAmount = _.filter(checkoutModel.get('quote').fees, (o) => {
            return o.type === constants.FEE_TYPES.RENTAL;
        });
        // if so, continue
        if (rentalAmount.length > 0) {
            // determine if there is a commission fee to subtract
            const commissionFee = _.get(
                checkoutModel.get('currentPaymentRequest'),
                'ppbFeeBreakdown.ppbCommissionFee.amount',
                0
            );
            // both rental amount and commission fee were found
            // subtract the values and then round according to the currency rounding function
            return _.round(rentalAmount[0].amount.amount - commissionFee, 2);
        }
        // rental amount was not found, exit
        return 0.0;
    },

    determineNightlyFees(checkoutModel) {
        // TODO isCesSupported Clean up
        if (
            checkoutModel.get('priceDetails') &&
            checkoutModel.get('priceDetails').averageNightlies
        ) {
            // averageNightlies may not be available (i.e. null) from priceDetails graphql call
            // depending on which type of request is being made (example: createCheckout vs addVas)
            const averageNightlies =
                checkoutModel.get('priceDetails').averageNightlies;
            const averageNightlyAmounts = averageNightlies.length
                ? averageNightlies.filter(
                      (nightly) => nightly.type === 'BASE_RENT'
                  )
                : null;
            return averageNightlyAmounts && averageNightlyAmounts.length > 0
                ? averageNightlyAmounts[0].perNightCost.amount
                : null;
        }

        const rentalFees = _.find(
            _.get(checkoutModel.get('quote'), 'fees', []),
            (fee) => fee.type === constants.FEE_TYPES.RENTAL
        );
        if (rentalFees) {
            return _.get(
                rentalFees,
                'checkoutAmounts.averageNightlyAmount.amount',
                null
            );
        }
        return null;
    },

    determineDisplayQuoteTotal(checkoutModel) {
        const pdQDT = this.getCSPriceDetailsEdapAttribute(
            checkoutModel,
            'displayquotetotal'
        );
        const parsedPDQDT = parseFloat(pdQDT);
        return Number.isNaN(parsedPDQDT) ? '' : parsedPDQDT;
    },

    determinePartnerFeesAmount(checkoutModel) {
        const pdPFA = this.getCSPriceDetailsEdapAttribute(
            checkoutModel,
            'partnerfeesamount'
        );
        const parsedPDPFA = parseFloat(pdPFA);
        return Number.isNaN(parsedPDPFA) ? -1 : parsedPDPFA;
    },

    determineServiceFeeAmount(checkoutModel) {
        const pdSFA = this.getCSPriceDetailsEdapAttribute(
            checkoutModel,
            'servicefeeamount'
        );
        const parsedPDSFA = parseFloat(pdSFA);
        return Number.isNaN(parsedPDSFA) ? 0.0 : parsedPDSFA;
    },

    determineOrderTax(checkoutModel) {
        const pdOT = this.getCSPriceDetailsEdapAttribute(
            checkoutModel,
            'ordertax'
        );
        const parsedPDOT = parseFloat(pdOT);
        return Number.isNaN(parsedPDOT) ? -1 : parsedPDOT;
    },

    determineOrderTotal(checkoutModel) {
        const pdOTax = this.getCSPriceDetailsEdapAttribute(
            checkoutModel,
            'ordertax'
        );
        const totalAmount = _.get(
            checkoutModel.get('quote'),
            'quoteTotals.totalAmount.amount',
            0.0
        );
        return this.calculateOrderTotal(pdOTax, totalAmount);
    },

    calculateOrderTotal(pdOTax, totalAmount) {
        const parsedPDOT = parseFloat(pdOTax);
        const tax = Number.isNaN(parsedPDOT) ? 0.0 : parsedPDOT;
        const total = Number.isNaN(totalAmount) ? 0.0 : totalAmount;
        // Remove tax from total
        // wiki: https://wiki.homeawaycorp.com/display/arch/ADL+Spec+-+Component-Specific+Fields+-+Traveler+Checkout+-+v1.10.0
        // we use the quoteTotals.totalAmount.amount from both AOE and the transformed CS payload
        // then we default to AOE tax and then if CS tax is available we use that to subtract
        return total - tax;
    },

    getRddAmount(model) {
        const pdRdd = this.getCSPriceDetailsEdapAttribute(model, 'rdd');
        const parsedRDD = parseFloat(pdRdd);
        return Number.isNaN(parsedRDD) ? -1 : parsedRDD;
    },

    getPdpAmount(model) {
        const pdPdp = this.getCSPriceDetailsEdapAttribute(model, 'pdp');
        if (pdPdp) {
            const parsedPDP = parseFloat(pdPdp);
            return isNaN(parsedPDP) ? -1 : parsedPDP;
        }

        // Check if PDP 1.0 is on the quote by looking at fees
        let pdp = _.find(
            _.get(model.get('quote'), 'fees', []),
            (fee) =>
                _.get(fee, 'type', '') ===
                constants.VAS.PROPERTY_DAMAGE_PROTECTION
        );
        if (pdp) {
            return pdp.amount.amount;
        }

        // PDP 1.5 will have ownerOptedDamageProtection on the Quote
        pdp = _.find(
            _.get(
                model.get('quote'),
                'ownerOptedDamageProtection.travelerOptions',
                []
            ),
            (fee) => fee.type === constants.VAS.CSA_PDP
        );
        if (pdp) {
            return pdp.cost.amount;
        }
        return -1;
    },

    getOwnerFees(model) {
        // TODO isCesSupported Clean up
        const pdNPF = this.getCSPriceDetailsEdapAttribute(
            model,
            'numpartnerfees'
        );
        if (pdNPF) {
            const parsedPDNPF = parseInt(pdNPF, 10);
            return isNaN(parsedPDNPF) ? 0 : parsedPDNPF;
        }

        const fees = _.get(model.get('quote'), 'fees');
        const ownerFeesApplicable = [
            constants.FEE_TYPES.LODGING_TAX,
            constants.FEE_TYPES.FEE,
            constants.FEE_TYPES.PET,
            constants.FEE_TYPES.ADDITIONAL_GUESTS_AMOUNT,
        ];
        let ownerFeeNumber = 0;

        if (!fees || !fees.length) {
            return ownerFeeNumber;
        }

        fees.map((fee) => {
            if (ownerFeesApplicable.indexOf(fee.type) !== -1) {
                ownerFeeNumber++;
            }
        });
        return ownerFeeNumber;
    },

    ipmNotCCOnly(checkoutModel) {
        const model = checkoutModel.get
            ? checkoutModel.toJSON()
            : checkoutModel;
        const isIPM = _.get(model, 'listing.integratedPropertyManager');
        const deferred =
            _.get(
                model,
                'quote.paymentOptions.paymentCollectionType',
                ''
            ).toUpperCase() === constants.paymentFormTypes.DEFERRED;
        const acceptedPaymentTypes = _.get(
            model,
            'quote.paymentOptions.acceptedPaymentTypes',
            []
        );

        return (
            isIPM &&
            (deferred ||
                acceptedPaymentTypes.length !== 1 ||
                acceptedPaymentTypes[0].toUpperCase() !==
                    constants.paymentFormTypes.CARD)
        );
    },

    getStayCollectedFeesList(stayCollectedFees) {
        return stayCollectedFees.stayCollectedFeePriceDetails &&
            Array.isArray(stayCollectedFees.stayCollectedFeePriceDetails) &&
            stayCollectedFees.stayCollectedFeePriceDetails.length
            ? stayCollectedFees.stayCollectedFeePriceDetails
            : [];
    },

    getUserAgent() {
        return window.navigator.userAgent;
    },

    getUserAgentData() {
        return window.navigator.userAgentData;
    },

    detectIE() {
        const ua = this.getUserAgent();
        return ua.search(/(MSIE|Trident|Edge)/) > -1;
    },

    detectEdgeV126() {
        const uad = this.getUserAgentData();
        return uad?.brands?.some(
            (brand) =>
                brand.brand === 'Microsoft Edge' && brand.version === '126'
        );
    },

    getStoredContactDetails() {
        return storage.get(
            this.isOlpCheckout()
                ? constants.STORAGE.USER_CONTACT_DETAILS_OLP
                : constants.STORAGE.USER_CONTACT_DETAILS
        );
    },

    getTSFDiscountEdapData(priceDetails) {
        if (!priceDetails) {
            return {};
        }
        const {lineItems} = priceDetails;
        const serviceFee = lineItems
            ? lineItems.find(
                  (item) => item.type === constants.FEE_TYPES.SERVICE_FEE
              )
            : {};
        if (serviceFee && serviceFee.discountTitle) {
            return {
                discountApplied: '1',
                discountLabel: `[\"${serviceFee.discountTitle}\"]`,
            };
        }
        return {
            discountApplied: '0',
            discountLabel: '[]',
        };
    },

    /**
     * Returns certain param if integratedPropertyManager(1) or platformPropertyManager(2) is true.
     * Otherwise returns FRBO(3), in documentation we also have Other(4) param, but for now we don't have such case.
     */
    getItem4Param() {
        const {IPM, PPM, FRBO} = constants.ITEM4;

        if (window?.TC?.checkoutModel?.get('listing')) {
            const {integratedPropertyManager, platformPropertyManager} =
                window.TC.checkoutModel.get('listing');
            if (integratedPropertyManager || platformPropertyManager) {
                return integratedPropertyManager ? IPM : PPM;
            }
        }

        return FRBO;
    },

    getPlatformSource() {
        return this.isNativeMobileIOS()
            ? constants.PLATFORM_SOURCE.IOS
            : this.isNativeMobileAndroid()
            ? constants.PLATFORM_SOURCE.ANDROID
            : constants.PLATFORM_SOURCE.DESKTOP;
    },

    shouldShowActFast(checkoutModel) {
        const countryCode = checkoutModel.get('countryCode');
        return !constants.EXCLUDED_URGENCY_MSG_COUNTRIES[countryCode];
    },

    updateInlineErrorMessage({selector, messageKey}) {
        requestAnimationFrame(() => {
            const inlineErrorMessageItem = document.querySelector(selector);
            if (inlineErrorMessageItem) {
                const inlineErrorMessage = inlineErrorMessageItem.parentElement;
                inlineErrorMessage.setAttribute('aria-live', 'polite');
                inlineErrorMessage.setAttribute('role', 'alert');

                inlineErrorMessageItem.setAttribute('aria-hidden', 'true');
                inlineErrorMessageItem.removeAttribute('aria-live');
                inlineErrorMessageItem.removeAttribute('role');

                const inlineErrorMessageSpan =
                    inlineErrorMessageItem.previousSibling;
                inlineErrorMessageSpan.setAttribute('aria-hidden', 'true');

                const inlineErrorMessageIcon =
                    inlineErrorMessageItem.previousSibling?.childNodes;
                inlineErrorMessageIcon?.forEach((el) => {
                    el.setAttribute('aria-hidden', 'true');
                });

                const span = document.createElement('span');
                span.classList.add('sr-only');
                span.textContent = `Error, ${message.read(messageKey)}`;
                inlineErrorMessage.appendChild(span);
            }
        });
    },
};

export default utility;
